// src/app/modules/demandes/BulkUsersFieldsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../../RessourcesStyles/UsersListModal.css";

const makeRow = (u) => ({
    targetUserId: u?.id,
    mode: "",
    period: "",
    niveau: "",
    certifiante: false,
    priorite: 2,
    objectifs: "",
    objectifs2: "",
    objectifs3: "",
});

const norm = (v) => String(v ?? "").trim().toLowerCase();

function userLabel(u) {
    const first = u?.firstName || u?.prenom || "";
    const last = u?.lastName || u?.nom || "";
    return `${first} ${last}`.trim() || u?.username || u?.login || `User#${u?.id ?? ""}`;
}


const PERIOD_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const NIVEAU_OPTIONS = ["Débutant", "Intermédiaire", "Avancé"];
const MODE_OPTIONS = ["Distanciel", "Présentiel", "Hybride"];

export default function BulkUsersFieldsModal({ open, onClose, users = [], onSubmit, title }) {
    const [rows, setRows] = useState([]);
    const [applyAll, setApplyAll] = useState(makeRow({ id: -1 }));
    const [filterUser, setFilterUser] = useState("");
    const [showApplyAll, setShowApplyAll] = useState(true);

    useEffect(() => {
        if (!open) return;
        setRows((users || []).map(makeRow));
        setApplyAll(makeRow({ id: -1 }));
        setFilterUser("");
        setShowApplyAll(true);
    }, [open, users]);

    const update = (idx, key, val) => {
        setRows((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [key]: val };
            return next;
        });
    };

    const applyToAll = () => {
        setRows((prev) =>
            prev.map((r) => ({
                ...r,
                mode: applyAll.mode,
                period: applyAll.period,
                niveau: applyAll.niveau,
                certifiante: !!applyAll.certifiante,
                priorite: Number(applyAll.priorite || 2),
                objectifs: applyAll.objectifs,
                objectifs2: applyAll.objectifs2,
                objectifs3: applyAll.objectifs3,
            }))
        );
    };

    const invalidCount = useMemo(() => {
        return (rows || []).filter((r) => {
            const p = Number(r?.priorite);
            return !r?.targetUserId || ![1, 2, 3].includes(p);
        }).length;
    }, [rows]);

    const filteredIndexes = useMemo(() => {
        const q = norm(filterUser);
        if (!q) return users.map((_, idx) => idx);

        const idxs = [];
        (users || []).forEach((u, idx) => {
            const name = norm(userLabel(u));
            const matricule = norm(u?.matricule);
            const email = norm(u?.email || u?.mail);
            const entity = norm(u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName);
            if (name.includes(q) || matricule.includes(q) || email.includes(q) || entity.includes(q)) idxs.push(idx);
        });
        return idxs;
    }, [users, filterUser]);

    const submit = () => {
        if (invalidCount > 0) return;
        onSubmit?.(rows);
    };

    if (!open) return null;

    return (
        <div className="modalOverlay" onMouseDown={onClose}>
            <div className="modalCard" style={{ width: "min(1200px, 100%)" }} onMouseDown={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <div>
                        <div className="modalTitle">{title || "Champs par utilisateur"}</div>
                        <div className="modalSubTitle">
                            {users?.length || 0} utilisateur(s)
                            {invalidCount > 0 ? ` — ${invalidCount} ligne(s) invalide(s)` : ""}
                        </div>
                    </div>
                    <button className="btn btn-secondary" type="button" onClick={onClose}>
                        Fermer
                    </button>
                </div>

                <div className="modalBody">
                    <div className="campagne-grid-3" style={{ marginBottom: 12 }}>
                        <div className="field" style={{ gridColumn: "1 / span 2" }}>
                            <label>Filtrer les utilisateurs</label>
                            <input
                                value={filterUser}
                                onChange={(e) => setFilterUser(e.target.value)}
                                placeholder="Nom, matricule, email, entité..."
                            />
                        </div>
                        <div className="field">
                            <label>Bloc “Appliquer à tous”</label>
                            <button className="btn btn-secondary" type="button" onClick={() => setShowApplyAll((v) => !v)}>
                                {showApplyAll ? "Masquer" : "Afficher"}
                            </button>
                        </div>
                    </div>

                    {showApplyAll && (
                        <div className="tableWrap" style={{ marginBottom: 12, padding: 12 }}>
                            <div className="campagne-grid-3">
                                <div className="field">
                                    <label>Période (all)</label>
                                    <select
                                        value={applyAll.period || ""}
                                        onChange={(e) => setApplyAll((s) => ({ ...s, period: e.target.value }))}
                                    >
                                        <option value="">—</option>
                                        {PERIOD_OPTIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="field">
                                    <label>Niveau (all)</label>
                                    <select
                                        value={applyAll.niveau || ""}
                                        onChange={(e) => setApplyAll((s) => ({ ...s, niveau: e.target.value }))}
                                    >
                                        <option value="">—</option>
                                        {NIVEAU_OPTIONS.map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="field">
                                    <label>Mode (all)</label>
                                    <select
                                        value={applyAll.mode || ""}
                                        onChange={(e) => setApplyAll((s) => ({ ...s, mode: e.target.value }))}
                                    >
                                        <option value="">—</option>
                                        {MODE_OPTIONS.map((m) => (
                                            <option key={m} value={m}>
                                                {m}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="campagne-grid-3" style={{ marginTop: 10 }}>
                                <div className="field">
                                    <label>Priorité (all)</label>
                                    <select
                                        value={applyAll.priorite}
                                        onChange={(e) => setApplyAll((s) => ({ ...s, priorite: Number(e.target.value) }))}
                                    >
                                        <option value={1}>1 - Haute</option>
                                        <option value={2}>2 - Moyenne</option>
                                        <option value={3}>3 - Basse</option>
                                    </select>
                                </div>

                                <div className="field">
                                    <label>Certifiante (all)</label>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, height: 42 }}>
                                        <input
                                            type="checkbox"
                                            checked={!!applyAll.certifiante}
                                            onChange={(e) => setApplyAll((s) => ({ ...s, certifiante: e.target.checked }))}
                                        />
                                        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Oui / Non</span>
                                    </div>
                                </div>

                                <div className="field" style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <label>&nbsp;</label>
                                    <button className="btn btn-primary" type="button" onClick={applyToAll}>
                                        Appliquer à tous
                                    </button>
                                </div>
                            </div>

                            <div className="campagne-grid-3" style={{ marginTop: 10 }}>
                                <div className="field">
                                    <label>Objectifs 1 (all)</label>
                                    <input value={applyAll.objectifs} onChange={(e) => setApplyAll((s) => ({ ...s, objectifs: e.target.value }))} />
                                </div>
                                <div className="field">
                                    <label>Objectifs 2 (all)</label>
                                    <input value={applyAll.objectifs2} onChange={(e) => setApplyAll((s) => ({ ...s, objectifs2: e.target.value }))} />
                                </div>
                                <div className="field">
                                    <label>Objectifs 3 (all)</label>
                                    <input value={applyAll.objectifs3} onChange={(e) => setApplyAll((s) => ({ ...s, objectifs3: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="tableWrap bulkFieldsWrap">
                        <table className="table bulkFieldsTable">
                            <thead className="stickyHead">
                            <tr>
                                <th style={{ minWidth: 220 }}>Utilisateur</th>
                                <th style={{ minWidth: 140 }}>Période</th>
                                <th style={{ minWidth: 160 }}>Niveau</th>
                                <th style={{ minWidth: 140 }}>Mode</th>
                                <th style={{ width: 90 }}>Certif.</th>
                                <th style={{ width: 90 }}>Priorité</th>
                                <th style={{ minWidth: 220 }}>Objectifs 1</th>
                                <th style={{ minWidth: 220 }}>Objectifs 2</th>
                                <th style={{ minWidth: 220 }}>Objectifs 3</th>
                            </tr>
                            </thead>

                            <tbody>
                            {filteredIndexes.length === 0 ? (
                                <tr>
                                    <td colSpan={9} style={{ padding: 16, color: "#6b7280" }}>
                                        Aucun utilisateur correspondant au filtre.
                                    </td>
                                </tr>
                            ) : (
                                filteredIndexes.map((idx) => {
                                    const u = users[idx];
                                    const r = rows[idx] || makeRow(u);
                                    const invalid = !r?.targetUserId || ![1, 2, 3].includes(Number(r?.priorite));

                                    return (
                                        <tr key={u?.id ?? `${userLabel(u)}-${idx}`} className={invalid ? "rowInvalid" : ""}>
                                            <td>
                                                <div style={{ fontWeight: 800, color: "#111827" }}>{userLabel(u)}</div>
                                                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
                                                    {u?.matricule ? `Mat: ${u.matricule}` : ""}
                                                    {u?.email || u?.mail ? ` ${u?.matricule ? "•" : ""} ${u?.email || u?.mail}` : ""}
                                                </div>
                                            </td>

                                            <td>
                                                <select value={r.period || ""} onChange={(e) => update(idx, "period", e.target.value)}>
                                                    <option value="">—</option>
                                                    {PERIOD_OPTIONS.map((p) => (
                                                        <option key={p} value={p}>
                                                            {p}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td>
                                                <select value={r.niveau || ""} onChange={(e) => update(idx, "niveau", e.target.value)}>
                                                    <option value="">—</option>
                                                    {NIVEAU_OPTIONS.map((n) => (
                                                        <option key={n} value={n}>
                                                            {n}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td>
                                                <select value={r.mode || ""} onChange={(e) => update(idx, "mode", e.target.value)}>
                                                    <option value="">—</option>
                                                    {MODE_OPTIONS.map((m) => (
                                                        <option key={m} value={m}>
                                                            {m}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td style={{ textAlign: "center" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!r.certifiante}
                                                    onChange={(e) => update(idx, "certifiante", e.target.checked)}
                                                />
                                            </td>

                                            <td>
                                                <select value={r.priorite ?? 2} onChange={(e) => update(idx, "priorite", Number(e.target.value))}>
                                                    <option value={1}>1</option>
                                                    <option value={2}>2</option>
                                                    <option value={3}>3</option>
                                                </select>
                                            </td>

                                            <td>
                                                <textarea rows={2} value={r.objectifs || ""} onChange={(e) => update(idx, "objectifs", e.target.value)} />
                                            </td>
                                            <td>
                                                <textarea rows={2} value={r.objectifs2 || ""} onChange={(e) => update(idx, "objectifs2", e.target.value)} />
                                            </td>
                                            <td>
                                                <textarea rows={2} value={r.objectifs3 || ""} onChange={(e) => update(idx, "objectifs3", e.target.value)} />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>

                    {invalidCount > 0 ? (
                        <div style={{ marginTop: 10, color: "#b45309", fontSize: 12, fontWeight: 700 }}>
                            Certaines lignes sont invalides (Priorité doit être 1..3). Corrige avant d’envoyer.
                        </div>
                    ) : null}
                </div>

                <div className="modalHeader" style={{ borderTop: "1px solid #e5e7eb", borderBottom: "none" }}>
                    <button className="btn btn-secondary" type="button" onClick={onClose}>
                        Annuler
                    </button>
                    <button className="btn btn-primary" type="button" onClick={submit} disabled={users.length === 0 || invalidCount > 0}>
                        Envoyer
                    </button>
                </div>
            </div>
        </div>
    );
}
